import { useNavigate } from 'react-router-dom';
import {
  Building2, ChevronRight, Globe, Layers,
  Settings, ShieldCheck, Truck, Wallet,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';

const isDevMode = !IS_FIREBASE_CONFIGURED;

// ── Hub definitions ──────────────────────────────────────────────────────────

interface HubDef {
  id: string;
  label: string;
  desc: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  path: string;
  /** Return true if this hub should be visible given current org settings. */
  isVisible: (org: Record<string, unknown> | null) => boolean;
}

const HUBS: HubDef[] = [
  {
    id: 'company',
    label: 'Company',
    desc: 'Profile, users, and billing.',
    icon: Building2,
    path: '/admin/company',
    isVisible: () => true,
  },
  {
    id: 'quoting',
    label: 'Quoting',
    desc: 'Rates, inventory, services, and quote settings.',
    icon: Layers,
    path: '/admin/quoting',
    isVisible: () => true,
  },
  {
    id: 'payments',
    label: 'Payments',
    desc: 'Payment methods, schedule, and policies.',
    icon: Wallet,
    path: '/admin/payments',
    isVisible: (org) =>
      isDevMode || (org?.featureCustomerPayments ?? false) as boolean,
  },
  {
    id: 'customer-facing',
    label: 'Customer-Facing',
    desc: 'Booking form and online quote portal.',
    icon: Globe,
    path: '/admin/customer-facing',
    isVisible: (org) =>
      isDevMode ||
      (org?.featureClientPortal !== false) ||
      ((org?.featureOnlineLeadCapture ?? false) as boolean),
  },
  {
    id: 'operations',
    label: 'Operations',
    desc: 'Fleet, dispatch, and job management.',
    icon: Truck,
    path: '/admin/operations',
    isVisible: () => true,
  },
  {
    id: 'platform',
    label: 'Platform',
    desc: 'Feature toggles and integrations.',
    icon: ShieldCheck,
    path: '/admin/platform',
    isVisible: () => true,
  },
];

// ── Hub card ─────────────────────────────────────────────────────────────────

function HubCard({ hub, onClick }: { hub: HubDef; onClick: () => void }) {
  const Icon = hub.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-[var(--color-surface)] border border-[var(--color-border)]
                 rounded-xl p-4 shadow-brand-sm
                 flex items-center gap-4
                 md:flex-col md:items-center md:text-center md:gap-3 md:p-6 md:min-h-[140px] md:justify-center
                 hover:border-plum/40 hover:shadow-brand-md active:bg-mist
                 transition-all duration-150"
    >
      <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center
                      md:w-12 md:h-12">
        <Icon size={20} className="text-plum" />
      </div>
      <div className="flex-1 min-w-0 md:flex-none md:w-full">
        <p className="font-semibold text-small text-[var(--color-text-primary)]">{hub.label}</p>
        <p className="text-[13px] text-[var(--color-text-secondary)] mt-0.5 leading-snug">{hub.desc}</p>
      </div>
      <ChevronRight size={18} className="shrink-0 text-slate md:hidden" />
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function NewAdminPanelPage() {
  const navigate = useNavigate();
  const { currentUser, adminOrg, loading, error } = useUserProfile();
  const { org: orgSettings } = useOrgSettings(adminOrg?.orgId ?? '');

  const isPlatformAdmin = currentUser?.isPlatformAdmin || isDevMode;
  const hasAnyAdminAccess = isPlatformAdmin || !!adminOrg;
  const orgData = orgSettings as unknown as Record<string, unknown> | null;

  const visibleHubs = HUBS.filter(h => h.isVisible(orgData));

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl md:mx-auto">
      <h1 className="font-display text-h1 text-[var(--color-text-primary)] mb-1">Admin Panel</h1>
      <p className="text-small text-[var(--color-text-secondary)] mb-6">Settings and tools for admins.</p>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && hasAnyAdminAccess && (
        <div className="flex flex-col gap-6">
          {/* Platform Admin Tools (super-admin only) */}
          {isPlatformAdmin && (
            <button
              type="button"
              onClick={() => navigate('/admin/platform-tools')}
              className="w-full text-left bg-plum rounded-xl p-4 shadow-brand-md
                         flex items-center gap-4
                         md:flex-col md:items-start md:gap-3 md:p-5
                         hover:bg-rose active:opacity-80
                         transition-all duration-150"
            >
              <div className="shrink-0 w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center
                              md:w-11 md:h-11">
                <ShieldCheck size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0 md:flex-none md:w-full">
                <p className="font-semibold text-small text-white">Platform Admin Tools</p>
                <p className="text-[13px] text-white/70 mt-0.5 leading-snug">
                  Manage users, organizations, and platform settings.
                </p>
              </div>
              <ChevronRight size={18} className="shrink-0 text-white/60 md:hidden" />
            </button>
          )}

          {/* Hub cards */}
          {adminOrg && (
            <div className="flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-4">
              {visibleHubs.map(hub => (
                <HubCard key={hub.id} hub={hub} onClick={() => navigate(hub.path)} />
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-20 gap-3 text-center">
          <Settings size={36} className="text-red-400" />
          <p className="text-small text-red-500">{error}</p>
        </div>
      )}

      {!loading && !error && !hasAnyAdminAccess && (
        <div className="flex flex-col items-center py-20 gap-3 text-center">
          <Settings size={36} className="text-stone" />
          <p className="text-small text-slate">No admin tools available for your account.</p>
        </div>
      )}

    </div>
  );
}
